import { DeleteOutlined, PauseCircleFilled, PlayCircleFilled, ReloadOutlined } from "@ant-design/icons";
import { Button, Modal, Progress, Tooltip, Upload, UploadFile } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalAudioLibraryApi } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { RootState } from "../app/store";
import { REACT_APP_MAX_UPLOAD_SIZE } from "../config";
import { clearErrorAudios, fetchAudios, showAudioError } from "../features/audioLibrarySlice";

import { deleteLibraryAudio, audioUploadUrl, apiConfiguration } from "../features/server";
import { authHeader } from "../authConfig/token";
import { Problem } from "../model/problem";
import { getErrorMessage } from "../utils/getErrorMessage";
import { readFileAsync } from "../utils/fileUtils";
import { bytesToSize } from "../utils/sizeUtis";


export const AudioLibraryPage = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const audios = useAppSelector((state: RootState) => state.audioLibrary.audios);
    const status = useAppSelector((state: RootState) => state.audioLibrary.status);
    const error = useAppSelector((state: RootState) => state.audioLibrary.error);
    const loaded = useAppSelector((state: RootState) => state.audioLibrary.loaded);
    const total = useAppSelector((state: RootState) => state.audioLibrary.total);
    const [currentAudioTrackUid, setCurrentTrack] = useState<string | undefined>();
    const [player, setPlayer] = useState(new Audio());
    const [isPlaying, setIsPlaying] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);



    useEffect(() => {
        dispatch(fetchAudios());
    }, []);


    // Audios stream in one-by-one from the store; keep the (controlled) upload
    // list in sync as each one arrives, carrying its preloaded content url.
    useEffect(() => {
        setFileList(audios?.map((audio) => ({
            uid: audio.audio.id,
            name: audio.audio.name,
            status: 'done',
            url: audio.url,
        } as UploadFile)) ?? []);
    }, [audios]);

    const isLoadingContent = total > 0 && loaded < total;
    const progressPercent = total > 0 ? Math.round((loaded / total) * 100) : 0;



    const errorMessage = getErrorMessage(error);

    const onReload = () => {
        player.pause();
        setIsPlaying(false);
        dispatch(fetchAudios());
    }

    const onDeleteFile = async (audioId: string) => {
        if (currentAudioTrackUid == audioId && !player.paused) {
            player.pause();
            setIsPlaying(false);
            return;
        }
        await deleteLibraryAudio(audioId);



    }


    const onBeforeUpload = (file: File) => {
        if (file.size > REACT_APP_MAX_UPLOAD_SIZE) {
            dispatch(showAudioError({
                type: 'file-is-too-big',
                title: t('error.fileTooBig.title'),
                detail: t('error.fileTooBig.detail', {
                    name: file.name,
                    limit: bytesToSize(REACT_APP_MAX_UPLOAD_SIZE)
                })
            } as Problem));
            return false;
        }

        return file;
    }


    const cacheAudio = async (file: UploadFile) => {
        const api = new GlobalAudioLibraryApi(apiConfiguration());
        const r = await api.apiV1AdministrationAudioLibraryIdContentGet(file.uid!, { responseType: 'blob' });
        const url = await readFileAsync(r.data);
        file.url = url as string;
    }


    const onPlay = async (file: UploadFile) => {
        if (file.url == null) {
            await cacheAudio(file);
        }
        if (currentAudioTrackUid == file.uid && !player.paused) {
            player.pause();
            setIsPlaying(false);
            return;
        }

        if (currentAudioTrackUid == file.uid && player.paused) {
            player.play();
            setIsPlaying(true);
            setCurrentTrack(file.uid);
            return;
        }
        player.pause();
        player.src = file.url!;
        player.play();
        setIsPlaying(true);
        setCurrentTrack(file.uid);
    }

    const audioElementRender = (
        _originNode: React.ReactElement,
        file: UploadFile,
        _fileList: Array<UploadFile>,
        actions: { download: () => void; preview: () => void; remove: () => void }
    ) => {
        const playing = currentAudioTrackUid === file.uid && isPlaying;
        return (
            <div className="audio-row">
                <button
                    type="button"
                    className={`audio-row__play${playing ? " is-playing" : ""}`}
                    onClick={() => { onPlay(file); }}
                    aria-label={file.name}
                >
                    {playing ? <PauseCircleFilled /> : <PlayCircleFilled />}
                </button>
                <span className="audio-row__name">{file.name}</span>
                <button
                    type="button"
                    className="audio-row__delete"
                    onClick={() => { actions.remove(); }}
                    aria-label={t("actions.delete")}
                >
                    <DeleteOutlined />
                </button>
            </div>
        );
    };

    const header = (
        <PageHeader
            title={t("menu.audioLibrary")}
            count={audios?.length}
            actions={
                <Tooltip title={t("actions.refresh")}>
                    <Button
                        type="text"
                        shape="circle"
                        aria-label={t("actions.refresh")}
                        icon={<ReloadOutlined />}
                        onClick={onReload}
                    />
                </Tooltip>
            }
        />
    );

    if (status === 'idle' || status === 'loading') {
        return <>{header}<TableSkeleton rows={4} /></>;
    }

    return (
        <>
            {header}

            {isLoadingContent && (
                <Progress
                    className="mb-xs"
                    percent={progressPercent}
                    status="active"
                    format={() => `${loaded}/${total}`}
                />
            )}

            <div className="card media-card">
                <Upload
                    accept=".mp3, .aac, .mp4, .wav"
                    fileList={fileList}
                    itemRender={audioElementRender}
                    action={audioUploadUrl}
                    headers={authHeader()}
                    onChange={(info) => {
                        if (info.file.size != null && info.file.size > REACT_APP_MAX_UPLOAD_SIZE) {
                            info.fileList.pop();
                            setFileList([...info.fileList]);
                            return;
                        }
                        if (info.file.status === 'done') {
                            info.file.uid = info.file.response.id;
                        }
                        setFileList([...info.fileList]);
                    }}
                    beforeUpload={onBeforeUpload}
                    withCredentials={true}
                    showUploadList={{ showPreviewIcon: false, showRemoveIcon: false }}
                    onRemove={(file) => {
                        onDeleteFile((file.uid));
                    }}
                >
                    <Button className="mb-s" type="primary">{t("actions.upload")}</Button>
                </Upload>
            </div>

            <Modal title={errorMessage.title} visible={status === 'error' || status === 'auth-error'} onOk={() => dispatch(clearErrorAudios())} cancelButtonProps={{ hidden: true }}>
                <p>{errorMessage.details}</p>
            </Modal>
        </>
    );
}
